import { Button, Space, Tag } from "antd";
import dayjs from "dayjs";
import { FrontDeskBooking } from "../../types";
import { displayConfirmationNumber } from "../../utils/groupReference";

interface BookingColumnHandlers {
  handlePrintReceipt: (id: string, payload: any) => void;
  handleOpenAllocate: (booking: FrontDeskBooking) => void;
  handleOpenEdit: (booking: FrontDeskBooking) => void;
  handleOpenCheckIn: (booking: FrontDeskBooking) => void;
  handleOpenCheckOut: (booking: FrontDeskBooking) => void;
  handleDeleteBooking: (booking: FrontDeskBooking) => void;
  handleOpenView: (
    booking: FrontDeskBooking & { is_group_parent?: boolean },
  ) => void;
  handleNoShow?: (booking: FrontDeskBooking) => void;
}

export const getBookingColumns = (handlers: BookingColumnHandlers) => [
  {
    title: "Confirmation",
    dataIndex: "confirmation_number",
    key: "confirmation_number",
    width: 150,
    render: (v: string) => v ? displayConfirmationNumber(v) : "N/A",
  },
  {
    title: "Guest",
    key: "guest",
    width: 170,
    render: (_: any, row: FrontDeskBooking) => (
      <div>
        <strong>{row.guest_name}</strong>
        <div style={{ fontSize: "11px", color: "#666" }}>
          Guests: {row.number_of_guests}
        </div>
        {row.extra_beds ? (
          <div
            style={{ fontSize: "11px", color: "#b7791f", fontWeight: "bold" }}
          >
            Extra Beds: {row.extra_beds}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    title: "Contact",
    key: "contact",
    width: 220,
    render: (_: any, row: FrontDeskBooking) => (
      <span>
        {row.email}
        <br />
        {row.phone}
      </span>
    ),
  },
  {
    title: "Stay",
    key: "stay",
    width: 170,
    render: (_: any, row: FrontDeskBooking) => (
      <span>
        {row.check_in_date
          ? dayjs(row.check_in_date).format("MMM D, YYYY")
          : "N/A"}{" "}
        to{" "}
        {row.check_out_date
          ? dayjs(row.check_out_date).format("MMM D, YYYY")
          : "N/A"}
      </span>
    ),
  },
  {
    title: "Room",
    key: "room",
    width: 140,
    render: (_: any, row: FrontDeskBooking & { allocated_room_display?: string }) => (
      <span>{row.allocated_room_display || row.room_number || "Not Allocated"}</span>
    ),
  },
  {
    title: "Source",
    dataIndex: "reservation_source",
    key: "reservation_source",
    width: 110,
    render: (v: string) => (
      <Tag color={v === "walk_in" ? "gold" : "blue"}>{v}</Tag>
    ),
  },
  {
    title: "Payment Option",
    key: "payment_plan",
    width: 170,
    render: (_: any, row: FrontDeskBooking & { is_group_child?: boolean }) => {
      const plan = row.payment_plan;
      const total = row.total_price;
      const deposit = Math.round(total * 0.3);
      const dueAtCheckin = total - deposit;
      const isGroupChild = row.is_group_child;

      if (row.reservation_source === "walk_in") {
        return (
          <div>
            <Tag color="purple">Walk-In (Pay at Desk)</Tag>
          </div>
        );
      }

      if (plan === "full") {
        return (
          <div>
            <Tag color="gold">Paid Full Upfront</Tag>
            {!isGroupChild && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#52c41a",
                  marginTop: "4px",
                  fontWeight: "600",
                }}
              >
                Remaining: LKR 0.00
              </div>
            )}
          </div>
        );
      }

      return (
        <div>
          <Tag color="blue">30% Deposit Plan</Tag>
          {!isGroupChild && (
            <div
              style={{
                fontSize: "11px",
                color: "#d46b08",
                marginTop: "4px",
                fontWeight: "600",
              }}
            >
              Due at Check-in: LKR{" "}
              {dueAtCheckin.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: "AC Status",
    dataIndex: "ac_status",
    key: "ac_status",
    width: 100,
    render: (v: string) => {
      let text = "Both";
      if (v === "ac") text = "AC";
      if (v === "non_ac") text = "Non-AC";
      return <Tag color={v === "ac" ? "cyan" : v === "non_ac" ? "volcano" : "default"}>{text}</Tag>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (v: string) => {
      const color =
        v === "checked_in"
          ? "green"
          : v === "checked_out"
            ? "blue"
            : v === "confirmed"
              ? "cyan"
              : v === "cancelled"
                ? "red"
                : v === "reserved"
                  ? "purple"
                  : v === "blocked"
                    ? "volcano"
                    : v === "no_show"
                      ? "magenta"
                      : "orange";
      return <Tag color={color}>{v}</Tag>;
    },
  },
  {
    title: "Actions",
    key: "actions",
    width: 320,
    render: (_: any, row: FrontDeskBooking & { is_group_parent?: boolean }) => {
      if (row.is_group_parent) {
        return null;
      }

      const isAllocated = !!row.room_id || !!row.room_number;

      return (
        <Space direction="vertical" size="small" align="start">
          <Space wrap>
            {row.status !== "cancelled" &&
              row.status !== "checked_out" &&
              row.status !== "blocked" &&
              row.status !== "no_show" && (
                <Button
                  size="small"
                  style={isAllocated ? { backgroundColor: '#52c41a', color: '#fff', borderColor: '#52c41a' } : {}}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleOpenAllocate(row);
                  }}
                >
                  {isAllocated ? "Allocated" : "Allocate"}
                </Button>
              )}
            {isAllocated &&
              (row.status === "confirmed" ||
                row.status === "pending" ||
                row.status === "reserved") && (
                <Button
                  size="small"
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleOpenCheckIn(row);
                  }}
                >
                  Check-In
                </Button>
              )}
            {handlers.handleNoShow &&
              (row.status === "pending" ||
                row.status === "confirmed" ||
                row.status === "reserved") && (
                <Button
                  size="small"
                  danger
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleNoShow!(row);
                  }}
                >
                  No-Show
                </Button>
              )}
            {isAllocated && row.status === "checked_in" && (
              <Button
                size="small"
                danger
                type="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.handleOpenCheckOut(row);
                }}
              >
                Check-Out
              </Button>
            )}
          </Space>
        </Space>
      );
    },
  },
];

export const getGuestColumns = () => [
  { title: "Name", dataIndex: "full_name", key: "full_name", width: 180 },
  { title: "Email", dataIndex: "email", key: "email", width: 220 },
  { title: "Phone", dataIndex: "phone", key: "phone", width: 130 },
  {
    title: "ID Type",
    dataIndex: "id_document_type",
    key: "id_document_type",
    width: 110,
  },
  {
    title: "ID Number",
    dataIndex: "id_document_number",
    key: "id_document_number",
    width: 160,
  },
  {
    title: "Total Stays",
    dataIndex: "total_stays",
    key: "total_stays",
    width: 100,
  },
  {
    title: "Last Stay",
    dataIndex: "last_stay_date",
    key: "last_stay_date",
    width: 130,
    render: (val: string) => (val ? dayjs(val).format("MMM D, YYYY") : "N/A"),
  },
];
